import type { EntityPatch } from '../../../history/types';
import { createExistingHistorySnapshot, createMissingHistorySnapshot } from '../../../history';
import { createSketchEntityReferenceHistoryPatch } from '../../../history/domain/SketchEntityReferenceHistoryAdapter';
import { createSketchEntityReferenceHistorySnapshot } from '../../../reference/sketch-entity-reference/history/SketchEntityReferenceHistorySnapshot';
import { InMemorySketchEntityReferenceRepository } from '../../../reference/sketch-entity-reference/repository/SketchEntityReferenceRepository';
import { SketchEntityReferenceController } from '../../../reference/sketch-entity-reference/SketchEntityReferenceController';
import { SketchEntityReferenceCheckpointRuntime } from '../../../reference/sketch-entity-reference/rebuild/SketchEntityReferenceCheckpointRuntime';
import { SketchEntityReferenceDiagnosticProjectionService } from '../../../reference/sketch-entity-reference/diagnostic/SketchEntityReferenceDiagnosticProjectionService';
import { isSketchRelationContainer } from '../../../sketch/solver/constraint/SketchRelationContainer';
import type { SketchRelationDefinition } from '../../../sketch/solver/constraint/relationDefinition/SketchRelationDefinition';
import type { CreateGeometryReferencesInput } from '../../../reference/sketch-entity-reference/model/SketchEntityReferenceTypes';
import type { SketchEntityReferenceRecord } from '../../../reference/sketch-entity-reference/model/SketchEntityReferenceRecord';
import type {
	CreateSketchEntityReferencesResult,
	RebuildSketchEntityReferencesResult,
} from '../../../reference/sketch-entity-reference/model/SketchEntityReferenceResults';
import type {
	SketchEntityReferenceRemoveRelationInput,
	SketchEntityReferenceRemoveRelationResult,
} from '../../../reference/sketch-entity-reference/lifecycle/SketchEntityReferenceLifecycleCoordinator';
import type { SketchEntityReferenceTopologyOwnerRebuildInput } from '../../../reference/sketch-entity-reference/rebuild/SketchEntityReferenceRebuildCoordinator';
import type {
	SketchEntityReferenceHistoryDeleteInput,
	SketchEntityReferenceHistoryRestoreInput,
} from '../../../reference/sketch-entity-reference/history/SketchEntityReferenceHistoryMutationService';
import type {
	SketchEntityReferencePreviewSession,
	SketchEntityReferenceSelectionResolveInput,
	SketchEntityReferenceAllInnerLoopsResolveInput,
} from '../../../reference/sketch-entity-reference/SketchEntityReferenceController';
import type { SketchEntityReferenceSelectionCandidate } from '../../../reference/sketch-entity-reference/command/selection/SketchEntityReferenceSelectionAdapter';
import type { RemoveGeometryReferencesResult } from '../../../../types/reference/sketch-entity-reference/RemoveGeometryReferencesService';
import type {
	FeatureTimelineCheckpointRebuildInput,
	TimelineReferenceCheckpointResult,
	TimelineReferenceSnapshot,
} from '../../../../types/feature/FeatureTimelineRebuild';
import type {
	SketchEntityReferenceDocumentRuntimeDependencies,
} from '../../../../types/viewer/SketchEntityReferenceDocumentRuntime';

export type {
	SketchEntityReferenceDocumentRuntimeDependencies,
} from '../../../../types/viewer/SketchEntityReferenceDocumentRuntime';

/**
 * 当前 Viewer 文档唯一的转换实体引用运行时。
 *
 * 该深模块统一持有 Repository、Controller、诊断投影和历史补偿，Viewer 只保留
 * 稳定任务 API。这样能够保证创建、删除、重建与释放始终操作同一份文档主状态。
 */
export class SketchEntityReferenceDocumentRuntime {
	private readonly repository = new InMemorySketchEntityReferenceRepository();
	private readonly host;
	private readonly controller;
	private readonly diagnosticProjectionService;
	/**
	 * 当前 Runtime 曾承载 Record 的目标草图索引。
	 *
	 * 该集合只用于整体替换前扩大旧状态捕获范围，不保存 Record，也不作为持久主状态。
	 * 用户创建、历史恢复和离线恢复都从唯一 Repository 派生更新该索引。
	 */
	private readonly knownRecordTargetSketchIds = new Set<string>();

	/**
	 * @param dependencies Viewer 文档组合根提供的稳定能力。
	 */
	public constructor(
		private readonly dependencies: SketchEntityReferenceDocumentRuntimeDependencies,
	) {
		this.host = dependencies.createControllerHost();
		this.controller = new SketchEntityReferenceController({
			repository: this.repository,
			host: this.host,
		});
		this.diagnosticProjectionService = new SketchEntityReferenceDiagnosticProjectionService({
			repository: this.repository,
			runtime: this.host,
		});
	}

	/** 供草图移动控制器复用同一 Controller 的解除引用能力。 */
	public get moveDetachPort() {
		return this.controller;
	}

	/** 创建命令级 Preview 会话。 */
	public createPreviewSession(): SketchEntityReferencePreviewSession | null {
		return this.controller.createPreviewSession();
	}

	/** 解析单个选择候选。 */
	public resolveSelectionCandidate(
		input: SketchEntityReferenceSelectionResolveInput,
	): Promise<SketchEntityReferenceSelectionCandidate | null> {
		return this.controller.resolveSelectionCandidate(input);
	}

	/** 解析全部 inner loop 候选。 */
	public resolveAllInnerLoopCandidates(
		input: SketchEntityReferenceAllInnerLoopsResolveInput,
	): Promise<readonly SketchEntityReferenceSelectionCandidate[]> {
		return this.controller.resolveAllInnerLoopCandidates(input);
	}

	/** 提交一次创建任务及其唯一历史事务。 */
	public commitReferences(
		input: CreateGeometryReferencesInput,
	): Promise<CreateSketchEntityReferencesResult> {
		const historySession = this.dependencies.beginHistorySession({
			label: '转换实体引用',
			commandType: 'sketch.convert-entities.create',
		});

		return this.controller.commitReferences(input).then(async (result) => {
			const hasError = result.diagnostics.some((diagnostic) => diagnostic.level === 'error');
			if (hasError || result.createdReferenceIds.length === 0) {
				historySession?.cancel();
				return result;
			}

			try {
				await historySession?.commit({
					extraPatches: this.createReferenceCreateHistoryPatches(result.createdReferences),
				});
				result.createdReferences.forEach((reference) => {
					this.knownRecordTargetSketchIds.add(reference.targetSketchId);
				});
				return result;
			} catch (historyError) {
				await this.rollbackCreateAfterHistoryFailure(
					result.createdReferenceIds,
					historyError,
				);
				throw historyError;
			}
		}).catch((error) => {
			historySession?.cancel();
			throw error;
		});
	}

	/** 删除引用关系及其唯一历史事务。 */
	public removeRelation(
		input: SketchEntityReferenceRemoveRelationInput,
	): Promise<SketchEntityReferenceRemoveRelationResult> {
		const removedConstraint = this.captureRemovedConstraint(input);
		const historySession = this.dependencies.beginHistorySession({
			label: '删除草图几何关系',
			commandType: 'sketch.property.relation.delete',
		});

		return this.controller.removeRelation(input).then(async (result) => {
			this.refreshDiagnostics(result.removedReferences.map(
				(reference) => reference.targetSketchId,
			));
			const hasError = result.diagnostics.some((diagnostic) => diagnostic.level === 'error');
			const hasMutation = result.removedConstraintIds.length > 0
				|| result.removedReferenceIds.length > 0
				|| (result.updatedReferences?.length ?? 0) > 0;
			if (hasError || !hasMutation) {
				historySession?.cancel();
				return result;
			}
			if (!this.dependencies.solveActiveSketchParametricRelations()) {
				this.restoreAfterHistoryFailure(result, removedConstraint);
				historySession?.cancel();
				return this.createParametricSolveFailureResult(result);
			}

			try {
				await historySession?.commit({
					extraPatches: this.createReferenceRemoveHistoryPatches(result),
				});
			} catch (historyError) {
				this.restoreAfterHistoryFailure(result, removedConstraint);
				throw historyError;
			}
			return result;
		}).catch((error) => {
			historySession?.cancel();
			throw error;
		});
	}

	/** 同步恢复历史记录并刷新派生诊断。 */
	public restoreFromHistorySync(input: SketchEntityReferenceHistoryRestoreInput): boolean {
		this.controller.restoreReferenceFromHistorySync(input);
		this.knownRecordTargetSketchIds.add(input.reference.targetSketchId);
		if (input.previousReference) {
			this.knownRecordTargetSketchIds.add(input.previousReference.targetSketchId);
		}
		this.refreshDiagnostics([
			input.reference.targetSketchId,
			...(input.previousReference ? [input.previousReference.targetSketchId] : []),
		]);
		return true;
	}

	/** 同步删除历史记录并刷新派生诊断。 */
	public deleteFromHistorySync(input: SketchEntityReferenceHistoryDeleteInput): boolean {
		this.controller.deleteReferenceFromHistorySync(input);
		if (input.previousReference) {
			this.refreshDiagnostics([input.previousReference.targetSketchId]);
		}
		return true;
	}

	/** 回滚已创建引用并刷新派生诊断。 */
	public rollbackCreated(
		referenceIds: readonly string[],
	): Promise<RemoveGeometryReferencesResult> {
		return this.controller.rollbackCreatedReferences(referenceIds).then((result) => {
			this.refreshDiagnostics(result.removedReferences.map(
				(reference) => reference.targetSketchId,
			));
			return result;
		});
	}

	/** 幂等激活自动生命周期监听。 */
	public ensureRuntimeActive(): boolean {
		this.controller.ensureRuntimeActive();
		return true;
	}

	/** 捕获冻结的时间线引用快照。 */
	public captureTimelineSnapshot(targetSketchIds: readonly string[]): TimelineReferenceSnapshot {
		return this.controller.captureTimelineRebuildReferences(targetSketchIds);
	}

	/**
	 * 用离线包 Record 整体替换当前文档引用主状态。
	 *
	 * 加载属于文档初始化，不创建 Undo/Redo 事务。这里先校验全部目标实体，再复用现有同步
	 * 历史变更能力维护 Repository 与目标 tag；任一步骤失败都会补偿回替换前状态。
	 *
	 * @param records 已由离线扩展解析器校验的完整引用 Record。
	 * @param targetSketchIds 当前文档全部草图持久 ID。
	 */
	public replacePersistentRecords(
		records: readonly SketchEntityReferenceRecord[],
		targetSketchIds: readonly string[],
	): void {
		const targetSketchIdSet = new Set(targetSketchIds);
		const previousTargetSketchIds = new Set([
			...this.knownRecordTargetSketchIds,
			...targetSketchIdSet,
		]);
		const previousRecords = this.captureTimelineSnapshot([...previousTargetSketchIds])
			.items
			.map((item) => structuredClone(item.reference));

		// 先完成全量目标预校验，坏包不得先清空当前正式仓库。
		records.forEach((reference) => {
			const targetEntity = targetSketchIdSet.has(reference.targetSketchId)
				? this.host.getSketchObject(reference.targetSketchId, reference.targetEntityId)
				: null;
			if (!targetEntity) {
				throw new Error(
					`恢复转换实体引用失败：目标草图实体不存在，referenceId=${reference.referenceId}，targetEntityId=${reference.targetEntityId}`,
				);
			}
		});

		const deletedPreviousRecords: SketchEntityReferenceRecord[] = [];
		const restoredRecords: SketchEntityReferenceRecord[] = [];
		try {
			previousRecords.forEach((previousReference) => {
				// 先登记补偿对象；即使后续派生诊断刷新抛错，Repository 也可能已经完成删除。
				deletedPreviousRecords.push(previousReference);
				this.deleteFromHistorySync({
					referenceId: previousReference.referenceId,
					previousReference,
				});
			});
			records.forEach((reference) => {
				const restoredReference = structuredClone(reference);
				// 同理先登记；历史变更服务会自行补偿半成品，外层删除保持幂等。
				restoredRecords.push(restoredReference);
				this.restoreFromHistorySync({
					reference: restoredReference,
					previousReference: null,
				});
			});
			this.refreshDiagnostics([
				...targetSketchIdSet,
				...previousRecords.map((reference) => reference.targetSketchId),
			]);
			this.knownRecordTargetSketchIds.clear();
			targetSketchIdSet.forEach((sketchId) => {
				this.knownRecordTargetSketchIds.add(sketchId);
			});
		} catch (error) {
			const compensationErrors: unknown[] = [];
			[...restoredRecords].reverse().forEach((reference) => {
				try {
					this.deleteFromHistorySync({
						referenceId: reference.referenceId,
						previousReference: reference,
					});
				} catch (compensationError) {
					compensationErrors.push(compensationError);
				}
			});
			deletedPreviousRecords.forEach((previousReference) => {
				try {
					this.restoreFromHistorySync({
						reference: previousReference,
						previousReference: null,
					});
				} catch (compensationError) {
					compensationErrors.push(compensationError);
				}
			});
			if (compensationErrors.length > 0) {
				throw new AggregateError(
					[error, ...compensationErrors],
					'恢复转换实体引用失败，且状态补偿未完整完成',
				);
			}
			throw error;
		}
	}

	/** 使用 checkpoint topology 重建正式引用。 */
	public rebuildTimelineCheckpoint(
		input: FeatureTimelineCheckpointRebuildInput,
	): Promise<TimelineReferenceCheckpointResult> {
		const runtime = new SketchEntityReferenceCheckpointRuntime({
			baseRuntime: this.host,
			owners: input.owners,
		});
		return this.controller.rebuildTimelineCheckpoint({
			snapshot: input.referenceSnapshot,
			targetSketchIds: input.targetSketchIds,
			runtime,
			propagationId: input.propagationId,
			isInputCurrent: input.isInputCurrent,
		}).then((result) => {
			if (input.isInputCurrent()) {
				this.refreshDiagnostics(input.targetSketchIds);
			}
			return result;
		});
	}

	/** 按 topology owner 重建 Convert Entities 引用。 */
	public async rebuildTopologyOwners(
		input: SketchEntityReferenceTopologyOwnerRebuildInput,
	): Promise<RebuildSketchEntityReferencesResult> {
		if (input.owners.length === 0) {
			return this.createEmptyRebuildResult();
		}
		const result = await this.controller.rebuildTopologyOwners(input);
		return result ?? this.createEmptyRebuildResult();
	}

	/** 刷新草图引用诊断投影。 */
	public refreshDiagnostics(sketchIds: readonly string[]): void {
		this.diagnosticProjectionService.refreshSketches([...new Set(sketchIds)]);
	}

	/** 读取草图引用诊断摘要。 */
	public getWarningSummary(sketchId: string) {
		return this.diagnosticProjectionService.getSketchWarningSummary(sketchId);
	}

	/** 释放诊断投影和 Controller 持有的全部订阅。 */
	public dispose(): void {
		this.knownRecordTargetSketchIds.clear();
		this.diagnosticProjectionService.dispose();
		this.controller.dispose();
	}

	/** 创建引用 Record 创建补丁。 */
	private createReferenceCreateHistoryPatches(
		references: readonly SketchEntityReferenceRecord[],
	): EntityPatch[] {
		return references.map((reference) => createSketchEntityReferenceHistoryPatch({
			entityId: reference.referenceId,
			operation: 'create',
			before: createMissingHistorySnapshot(),
			after: createExistingHistorySnapshot(
				createSketchEntityReferenceHistorySnapshot(reference),
			),
			metadata: { sketchId: reference.targetSketchId },
		}));
	}

	/** 创建关系删除产生的 Record 补丁。 */
	private createReferenceRemoveHistoryPatches(
		result: SketchEntityReferenceRemoveRelationResult,
	): EntityPatch[] {
		const removedPatches = result.removedReferences.map((reference) => (
			createSketchEntityReferenceHistoryPatch({
				entityId: reference.referenceId,
				operation: 'delete',
				before: createExistingHistorySnapshot(
					createSketchEntityReferenceHistorySnapshot(reference),
				),
				after: createMissingHistorySnapshot(),
				metadata: { sketchId: reference.targetSketchId },
			})
		));
		const updatedPatches = (result.updatedReferences ?? []).map(({ before, after }) => (
			createSketchEntityReferenceHistoryPatch({
				entityId: before.referenceId,
				operation: 'update',
				before: createExistingHistorySnapshot(
					createSketchEntityReferenceHistorySnapshot(before),
				),
				after: createExistingHistorySnapshot(
					createSketchEntityReferenceHistorySnapshot(after),
				),
				metadata: { sketchId: before.targetSketchId },
			})
		));
		return [...removedPatches, ...updatedPatches];
	}

	/** 历史提交失败后通过统一删除入口回滚创建。 */
	private async rollbackCreateAfterHistoryFailure(
		referenceIds: readonly string[],
		historyError: unknown,
	): Promise<void> {
		const rollbackResult = await this.controller.rollbackCreatedReferences(referenceIds);
		const incompleteReferenceIds = referenceIds.filter((referenceId) => (
			rollbackResult.items.find((item) => item.referenceId === referenceId)?.status !== 'removed'
		));
		const hasRollbackError = rollbackResult.diagnostics.some(
			(diagnostic) => diagnostic.level === 'error',
		);
		if (incompleteReferenceIds.length === 0 && !hasRollbackError) {
			return;
		}
		throw new Error(
			`转换实体引用历史提交失败且创建回滚不完整：referenceIds=${incompleteReferenceIds.join(',') || referenceIds.join(',')}；history=${historyError instanceof Error ? historyError.message : String(historyError)}`,
		);
	}

	/** 关系删除历史提交失败后恢复领域状态。 */
	private restoreAfterHistoryFailure(
		result: SketchEntityReferenceRemoveRelationResult,
		removedConstraint: SketchRelationDefinition | null,
	): void {
		result.removedReferences.forEach((reference) => {
			this.controller.restoreReferenceFromHistorySync({
				reference,
				previousReference: null,
			});
		});
		(result.updatedReferences ?? []).forEach(({ before, after }) => {
			this.controller.restoreReferenceFromHistorySync({
				reference: before,
				previousReference: after,
			});
		});

		const hasReferenceMutation = result.removedReferences.length > 0
			|| (result.updatedReferences?.length ?? 0) > 0;
		if (!hasReferenceMutation && removedConstraint) {
			removedConstraint.getTargetsSnapshot().forEach((target) => {
				const container = Reflect.get(target, 'constraints');
				if (isSketchRelationContainer(container)) {
					container.addRelation(removedConstraint);
				}
			});
		}
		this.refreshDiagnostics([
			...result.removedReferences.map((reference) => reference.targetSketchId),
			...(result.updatedReferences ?? []).map(({ before }) => before.targetSketchId),
		]);
	}

	/**
	 * 在删除前捕获关系实例，供普通关系求解/历史失败时恢复全部目标容器。
	 *
	 * @param input 删除任务输入。
	 * @returns 当前关系实例；目标不支持关系容器或关系不存在时返回 null。
	 */
	private captureRemovedConstraint(
		input: SketchEntityReferenceRemoveRelationInput,
	): SketchRelationDefinition | null {
		const container = Reflect.get(input.targetEntity, 'constraints');
		if (!isSketchRelationContainer(container)) {
			return null;
		}
		return container.getRelation(input.constraintId) ?? null;
	}

	/**
	 * 创建“删除已回滚”的标准失败结果。
	 *
	 * @param result 已执行但随后恢复的删除结果。
	 * @returns 不再声明任何成功 mutation 的失败结果。
	 */
	private createParametricSolveFailureResult(
		result: SketchEntityReferenceRemoveRelationResult,
	): SketchEntityReferenceRemoveRelationResult {
		return {
			removedReferenceIds: [],
			removedReferences: [],
			updatedReferences: [],
			removedConstraintIds: [],
			referenceRelationRemoved: false,
			diagnostics: [
				...result.diagnostics,
				{
					kind: 'relation-solve-failed',
					level: 'error',
					message: '删除关系后的统一参数求解失败，已恢复删除前状态。',
				},
			],
		};
	}

	/** 创建空的标准重建结果。 */
	private createEmptyRebuildResult(
		diagnostics: RebuildSketchEntityReferencesResult['diagnostics'] = [],
	): RebuildSketchEntityReferencesResult {
		return {
			keptReferenceIds: [],
			removedReferenceIds: [],
			failedReferenceIds: [],
			diagnostics,
		};
	}
}
